import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { referDoctorService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', hindiName: '', marathiName: '', specialization: '', education: '', address: '', mobileNumber: '' };

const ReferDoctorManagement = () => {
  const navigate = useNavigate();
  const [referDoctors, setReferDoctors] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await referDoctorService.getAllReferDoctors();
      setReferDoctors(res.data);
    } catch (err) {
      setError('Failed to load refer doctors');
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        hindiName: item.hindiName || '',
        marathiName: item.marathiName || '',
        specialization: item.specialization || '',
        education: item.education || '',
        address: item.address || '',
        mobileNumber: item.mobileNumber || '',
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
    setError('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await referDoctorService.updateReferDoctor(editingItem.id, { ...formData, id: editingItem.id });
      } else {
        await referDoctorService.createReferDoctor(formData);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError('Failed to save refer doctor');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this doctor?')) {
      try {
        await referDoctorService.deleteReferDoctor(id);
        loadData();
      } catch (err) {
        setError('Failed to delete refer doctor');
      }
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Doctor Name', flex: 1, minWidth: 150 },
    { field: 'hindiName', headerName: 'Name (Hindi)', flex: 1, minWidth: 150, renderCell: (p) => p.value || '—' },
    { field: 'marathiName', headerName: 'Name (Marathi)', flex: 1, minWidth: 150, renderCell: (p) => p.value || '—' },
    { field: 'specialization', headerName: 'Specialization', flex: 1, minWidth: 130 },
    { field: 'mobileNumber', headerName: 'Mobile', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)} title="Edit">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)} title="Delete">
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Refer Doctor Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Refer Doctor
            </Button>
          </Box>

          <DataGrid
            rows={referDoctors}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Refer Doctor' : 'Add Refer Doctor'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Doctor Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Doctor Name (Hindi) — optional"
                  name="hindiName"
                  value={formData.hindiName}
                  onChange={handleChange}
                  placeholder="हिंदी नाम यहाँ दर्ज करें"
                  inputProps={{ lang: 'hi' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Doctor Name (Marathi) — optional"
                  name="marathiName"
                  value={formData.marathiName}
                  onChange={handleChange}
                  placeholder="मराठी नाव येथे प्रविष्ट करा"
                  inputProps={{ lang: 'mr' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Specialization"
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Education / Qualification"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default ReferDoctorManagement;
